import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api, type AdminLink } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { formatDuration, formatMetres, formatEpoch, entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const emptyForm = {
  id: '',
  text: '',
  from_device_id: '',
  to_device_id: '',
  travel_time: '',
  avg_travel_time: '',
  base_travel_time: '',
  link_length: '',
  speed_limit: '',
  min_travel_time: '',
  allowed_variance: '',
  time_arrived: '',
  project: '',
  default_travel_time: '',
}

type FormState = typeof emptyForm

function linkFromForm(form: FormState): Partial<AdminLink> {
  const num = (v: string) => (v === '' ? null : Number(v))
  return {
    id: num(form.id) as number,
    text: form.text || null,
    from_device_id: num(form.from_device_id),
    to_device_id: num(form.to_device_id),
    travel_time: num(form.travel_time),
    avg_travel_time: num(form.avg_travel_time),
    base_travel_time: num(form.base_travel_time),
    link_length: num(form.link_length),
    speed_limit: num(form.speed_limit),
    min_travel_time: num(form.min_travel_time),
    allowed_variance: num(form.allowed_variance),
    time_arrived: num(form.time_arrived),
    project: form.project || null,
    default_travel_time: num(form.default_travel_time),
  }
}

function formFromLink(link: AdminLink): FormState {
  const s = (v: number | string | null | undefined) => (v == null ? '' : String(v))
  return {
    id: s(link.id),
    text: link.text ?? '',
    from_device_id: s(link.from_device_id),
    to_device_id: s(link.to_device_id),
    travel_time: s(link.travel_time),
    avg_travel_time: s(link.avg_travel_time),
    base_travel_time: s(link.base_travel_time),
    link_length: s(link.link_length),
    speed_limit: s(link.speed_limit),
    min_travel_time: s(link.min_travel_time),
    allowed_variance: s(link.allowed_variance),
    time_arrived: s(link.time_arrived),
    project: link.project ?? '',
    default_travel_time: s(link.default_travel_time),
  }
}

export function AdminLinksPage() {
  const auth = useAuth()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [projectFilter, setProjectFilter] = useState('')

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectedLink, setSelectedLink] = useState<AdminLink | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [mutationError, setMutationError] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (projectFilter) params.project = projectFilter
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-links', page, pageSize, projectFilter],
    queryFn: () => api.adminListLinks(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  const createMutation = useMutation({
    mutationFn: (d: Partial<AdminLink>) => api.adminCreateLink(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-links'] })
      setCreateOpen(false)
      setForm(emptyForm)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: number; data: Partial<AdminLink> }) => api.adminUpdateLink(id, d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-links'] })
      setEditOpen(false)
      setSelectedLink(null)
      setForm(emptyForm)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.adminDeleteLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-links'] })
      setDeleteOpen(false)
      setSelectedLink(null)
      setMutationError('')
    },
    onError: (e: Error) => setMutationError(e.message),
  })

  const openCreate = () => { setForm(emptyForm); setMutationError(''); setCreateOpen(true) }
  const openEdit = (link: AdminLink) => { setSelectedLink(link); setForm(formFromLink(link)); setMutationError(''); setEditOpen(true) }
  const openDelete = (link: AdminLink) => { setSelectedLink(link); setMutationError(''); setDeleteOpen(true) }

  const updateField = (field: keyof FormState, value: string) => setForm((f) => ({ ...f, [field]: value }))

  const formFields = (idDisabled: boolean) => {
    const textFields: Array<{ key: keyof FormState; label: string; type: string }> = [
      { key: 'id', label: 'ID', type: 'number' },
      { key: 'text', label: 'Text', type: 'text' },
      { key: 'from_device_id', label: 'From Device ID', type: 'number' },
      { key: 'to_device_id', label: 'To Device ID', type: 'number' },
      { key: 'travel_time', label: 'Travel Time', type: 'number' },
      { key: 'avg_travel_time', label: 'Avg Travel Time', type: 'number' },
      { key: 'base_travel_time', label: 'Base Travel Time', type: 'number' },
      { key: 'link_length', label: 'Link Length', type: 'number' },
      { key: 'speed_limit', label: 'Speed Limit', type: 'number' },
      { key: 'min_travel_time', label: 'Min Travel Time', type: 'number' },
      { key: 'allowed_variance', label: 'Allowed Variance', type: 'number' },
      { key: 'time_arrived', label: 'Time Arrived', type: 'number' },
      { key: 'project', label: 'Project', type: 'text' },
      { key: 'default_travel_time', label: 'Default Travel Time', type: 'number' },
    ]

    return (
      <div className="grid gap-3 py-4 max-h-[60vh] overflow-y-auto">
        {textFields.map(({ key, label, type }) => (
          <div key={key} className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right text-sm">{label}</Label>
            <Input
              type={type}
              className="col-span-3"
              disabled={key === 'id' && idDisabled}
              value={form[key]}
              onChange={(e) => updateField(key, e.target.value)}
            />
          </div>
        ))}
        {mutationError && (
          <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
            {mutationError}
          </div>
        )}
      </div>
    )
  }

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Links</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Project</Label>
            <Input
              placeholder="Filter by project"
              className="w-48"
              value={projectFilter}
              onChange={(e) => { setProjectFilter(e.target.value); setPage(1) }}
            />
          </div>
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" /> Add New
          </Button>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading links...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading links: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Text</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">From Device</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">To Device</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Travel Time</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Avg TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Base TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Link Length</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Speed Limit</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Min TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Variance</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Time Arrived</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Project</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Default TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} className="text-center text-muted-foreground">No links found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell className="font-medium">
                          <RouterLink to={buildAppHref(`/admin/links/${link.id}`, auth)} className={entityRefClass}>{link.id}</RouterLink>
                        </TableCell>
                        <TableCell>{link.text ?? '—'}</TableCell>
                        <TableCell>
                          {link.from_device_id != null ? (
                            <RouterLink to={buildAppHref(`/admin/devices/${link.from_device_id}`, auth)} className={entityRefClass}>#{link.from_device_id}</RouterLink>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {link.to_device_id != null ? (
                            <RouterLink to={buildAppHref(`/admin/devices/${link.to_device_id}`, auth)} className={entityRefClass}>#{link.to_device_id}</RouterLink>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{formatDuration(link.travel_time)}</TableCell>
                        <TableCell>{formatDuration(link.avg_travel_time)}</TableCell>
                        <TableCell>{formatDuration(link.base_travel_time)}</TableCell>
                        <TableCell>{formatMetres(link.link_length)}</TableCell>
                        <TableCell>{link.speed_limit ?? '—'}</TableCell>
                        <TableCell>{formatDuration(link.min_travel_time)}</TableCell>
                        <TableCell>{formatDuration(link.allowed_variance)}</TableCell>
                        <TableCell>{formatEpoch(link.time_arrived)}</TableCell>
                        <TableCell>{link.project ?? '—'}</TableCell>
                        <TableCell>{formatDuration(link.default_travel_time)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(link)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDelete(link)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Link</DialogTitle>
            </DialogHeader>
            {formFields(false)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate(linkFromForm(form))} disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Link #{selectedLink?.id}</DialogTitle>
            </DialogHeader>
            {formFields(true)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button
                onClick={() => selectedLink && updateMutation.mutate({ id: selectedLink.id, data: linkFromForm(form) })}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Link</DialogTitle>
            </DialogHeader>
            <p className="py-4">Are you sure you want to delete Link #{selectedLink?.id}?</p>
            {mutationError && (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
                {mutationError}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => selectedLink && deleteMutation.mutate(selectedLink.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
